import React, { useRef, useEffect, useState } from 'react';
import { Cpu, UserCheck, Sun, Moon, ShoppingBag, Search, X, Scale, LogOut, Menu } from 'lucide-react';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';

const Navbar = ({ 
  setView, view, isAdmin, toggleDarkMode, isDarkMode, 
  setIsCartOpen, cartCount, searchQuery, setSearchQuery, 
  products, openProduct, compareCount,
  currency, setCurrency, currencies, formatPrice,
  isLoggedIn, setIsLoggedIn, setShowLoginModal, setShowAccountModal, buyerInfo
}) => {
  const [searchInput, setSearchInput] = useState(searchQuery || '');
  const { suggestions, showSuggestions, setShowSuggestions } = useSearchSuggestions(searchQuery, products);
  const searchRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Synchroniser searchInput avec searchQuery
  useEffect(() => {
    setSearchInput(searchQuery || '');
  }, [searchQuery]);

  // Fonction pour valider la recherche
  const handleSearch = () => {
    setSearchQuery(searchInput);
    setShowSuggestions(false);
    if (view !== 'catalog') setView('catalog');
  };

  // Gérer la touche Entrée
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setIsSearchFocused(false);
      e.currentTarget.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchFocused(false);
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowSuggestions]);

  const handleNavClick = (target, requiresAuth = false) => {
    setIsMobileMenuOpen(false);
    if (requiresAuth && !isLoggedIn) {
      setShowLoginModal(true);
    } else {
      setView(target);
    }
  };

  return (
    <nav className="fixed top-0 w-full z-[9999] bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b dark:border-slate-800 px-2 md:px-4 lg:px-6 h-16 md:h-20 flex items-center justify-between transition-all pointer-events-auto">
      <div className="flex items-center space-x-2 md:space-x-3 cursor-pointer group" onClick={() => setView('home')}>
        <div className="bg-slate-900 dark:bg-blue-600 p-2 md:p-2.5 rounded-xl md:rounded-2xl group-hover:rotate-12 transition-transform shadow-lg">
          <Cpu className="text-white w-5 h-5 md:w-6 md:h-6" />
        </div>
        <span className="hidden sm:block text-xl lg:text-2xl font-black tracking-tight text-slate-900 dark:text-white select-none">DEEPVISION HUB</span>
      </div>

      {/* Barre de recherche globale - Hidden on very small screens */}
      <div className="hidden md:block flex-1 max-w-md mx-4 lg:mx-8 relative" ref={searchRef}>
        <div className="relative">
          <input 
            type="text" 
            placeholder="Rechercher... (Entrée)" 
            className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-2xl py-2.5 pl-10 pr-20 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm" 
            value={searchInput} 
            onChange={(e) => {
              setSearchInput(e.target.value);
              setShowSuggestions(e.target.value.length > 1);
            }}
            onFocus={() => {
              setIsSearchFocused(true);
              if (searchQuery.length > 1) setShowSuggestions(true);
            }}
            onBlur={() => {
              setIsSearchFocused(false);
              setShowSuggestions(false);
            }}
            onKeyDown={handleSearchKeyDown}
          />
          <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
          <button
            onClick={handleSearch}
            className="absolute right-2 top-1.5 p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            title="Rechercher"
          >
            <Search size={16} />
          </button>
          {searchInput && (
            <button 
              onClick={() => { setSearchInput(''); setSearchQuery(''); }}
              className="absolute right-12 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {isSearchFocused && showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-[140] w-full mt-2 bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-2">
              {suggestions.map(product => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => {
                    openProduct(product);
                    setIsSearchFocused(false);
                    setShowSuggestions(false);
                    setSearchQuery(product.label);
                  }}
                  className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-colors text-left group"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                    <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-xs truncate group-hover:text-blue-600 transition-colors">{product.label}</p>
                    <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{product.brand}</p>
                  </div>
                  <div className="text-[10px] font-black text-blue-600">{formatPrice(product.price)}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-6">
        {/* Desktop Links */}
        <div className="hidden md:flex items-center space-x-6 relative z-50">
          <button 
            type="button"
            onClick={() => handleNavClick('catalog')}
            className={`text-sm font-black uppercase transition-all cursor-pointer hover:text-blue-600 ${view === 'catalog' ? 'text-blue-600' : 'text-slate-600 dark:text-slate-400'}`}
          >
            CATALOGUE
          </button>
          <button 
            type="button"
            onClick={() => handleNavClick('favorites', true)}
            className={`text-sm font-black uppercase transition-all cursor-pointer hover:text-blue-600 ${view === 'favorites' ? 'text-blue-600' : 'text-slate-600 dark:text-slate-400'}`}
          >
            FAVORIS
          </button>
          <button 
            type="button"
            onClick={() => handleNavClick('my-orders', true)}
            className={`text-sm font-black uppercase transition-all cursor-pointer hover:text-blue-600 ${view === 'my-orders' ? 'text-blue-600' : 'text-slate-600 dark:text-slate-400'}`}
          >
            COMMANDES
          </button>
          
          <button 
            onClick={() => setView('compare')} 
            className={`relative p-2 rounded-xl transition-all hover:bg-slate-100 dark:hover:bg-slate-800 ${view === 'compare' ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' : ''}`}
            title="Comparer les produits"
          >
            <Scale className="w-5 h-5" />
            {compareCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-black">
                {compareCount}
              </span>
            )}
          </button>
        </div>

        {isAdmin && (
          <div className="hidden md:flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 p-1 rounded-2xl border border-blue-100 dark:border-blue-800">
            <button 
              onClick={() => setView('admin')} 
              className={`p-2 rounded-xl transition-all flex flex-col items-center gap-1 ${view === 'admin' ? 'text-blue-600' : 'text-slate-400 hover:text-blue-600'}`}
              title="Administration"
            >
              <UserCheck className="w-5 h-5" />
              <span className="text-[8px] font-black uppercase tracking-tighter">Console</span>
            </button>
            <button 
              onClick={() => setIsLoggedIn()}
              className="p-2 text-slate-400 hover:text-red-500 transition-colors"
              title="Quitter l'administration"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Sélecteur de Devise - Hidden on mobile */}
        <div className="hidden sm:block relative group/currency">
          <button className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all">
            {currency} {currencies[currency]?.symbol}
          </button>
          <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden opacity-0 invisible group-hover/currency:opacity-100 group-hover/currency:visible transition-all z-[120]">
            <div className="p-2 max-h-[300px] overflow-y-auto">
              {Object.entries(currencies).map(([code, data]) => (
                <button
                  key={code}
                  onClick={() => setCurrency(code)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${currency === code ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
                >
                  <span>{data.label}</span>
                  <span>{data.symbol}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button onClick={toggleDarkMode} className="p-2 transition-all hover:scale-110 active:scale-95">
          {isDarkMode ? <Sun className="text-amber-400 w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {isLoggedIn ? (
          <div className="hidden sm:flex items-center gap-2">
            <button 
              onClick={() => setShowAccountModal && setShowAccountModal(true)}
              className="flex items-center gap-2 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
              title="Mon Compte"
            >
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-black text-xs">
                {buyerInfo.name 
                  ? buyerInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                  : (buyerInfo.email ? buyerInfo.email[0].toUpperCase() : 'U')}
              </div>
            </button>
            <button 
              onClick={() => setIsLoggedIn()}
              className="p-2 text-slate-400 hover:text-red-500 transition-colors"
              title="Déconnexion"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <button 
            onClick={() => setShowLoginModal(true)}
            className="hidden sm:block bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all"
          >
            Connexion
          </button>
        )}

        <button 
          onClick={() => setIsCartOpen(true)} 
          className="relative p-2 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 transition-all active:scale-95"
        >
          <ShoppingBag className="w-5 h-5" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-blue-600 dark:bg-blue-400 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900 font-black">
              {cartCount}
            </span>
          )}
        </button>

        {/* Mobile Menu Toggle */}
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 transition-all"
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-20 z-[90] bg-white dark:bg-slate-900 animate-in slide-in-from-top duration-300 md:hidden overflow-y-auto">
          <div className="p-6 space-y-8">
            <div className="space-y-4">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Navigation</p>
              <div className="grid grid-cols-1 gap-4">
                <button onClick={() => handleNavClick('home')} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl font-bold">Accueil</button>
                <button onClick={() => handleNavClick('catalog')} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl font-bold">Catalogue</button>
                <button onClick={() => handleNavClick('favorites', true)} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl font-bold">Mes Favoris</button>
                <button onClick={() => handleNavClick('my-orders', true)} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl font-bold">Mes Commandes</button>
                <button onClick={() => handleNavClick('compare')} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl font-bold">Comparateur ({compareCount})</button>
              </div>
            </div>

            {isAdmin && (
              <div className="space-y-4">
                <p className="text-[10px] font-black uppercase text-blue-600 tracking-[0.2em]">Administration</p>
                <button onClick={() => { setView('admin'); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-2xl font-bold">
                  <UserCheck className="w-5 h-5" /> Console Admin
                </button>
              </div>
            )}

            <div className="space-y-4">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Compte & Préférences</p>
              <div className="grid grid-cols-1 gap-4">
                {!isLoggedIn ? (
                  <button onClick={() => { setShowLoginModal(true); setIsMobileMenuOpen(false); }} className="flex items-center gap-4 p-4 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-2xl font-bold justify-center">Connexion</button>
                ) : (
                  <button onClick={() => { setIsLoggedIn(); setIsMobileMenuOpen(false); }} className="flex items-center gap-4 p-4 bg-red-50 text-red-600 rounded-2xl font-bold justify-center">Déconnexion</button>
                )}
                
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                  <span className="font-bold">Devise</span>
                  <select 
                    value={currency} 
                    onChange={(e) => setCurrency(e.target.value)}
                    className="bg-transparent font-black text-blue-600 outline-none"
                  >
                    {Object.keys(currencies).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;

